<template>
  <div
    class="vertical-card"
    :class="{ 'h-full': shouldStretchHeight }"
    :style="[verticalAlignStyle]"
  >
    <!-- Карточка с контентом -->
    <div
      class="vertical-card-content"
      :class="{ 'h-full': shouldStretchHeight }"
      :style="[
         CssStyleHelpers.background(
        props.card?.backgroundColorField?.data?.value,
        props.card?.backgroundColorToggleField?.data?.value
      ),
        CssStyleHelpers.backgroundImage(
          props.card?.backgroundMediaField?.data?.url,
          props.card?.backgroundMediaToggleField?.data?.value
        ),
        CssStyleHelpers.borderRadius(cardsGroup.cardsBorderRadius),
        CssStyleHelpers.border(
          props.card?.borderField?.data,
          props.card?.borderField?.data?.enabled
        ),
        CssStyleHelpers.paddings(cardsGroup.cardsInnerSpacing),
      ]"
    >
      <!-- Медиа секция -->
      <div
        v-if="mainGroup.isShowCardMedia && hasMediaContent"
        class="vertical-card-media overflow-hidden"
        :style="{
          ...CssStyleHelpers.filterShadow(props.card?.shadowField?.data),
          ...CssStyleHelpers.borderRadius(cardsGroup.imageBorderRadius),
          ...CssStyleHelpers.paddings(props.card?.mediaPaddingsField?.data),
          ...(shouldStretchHeight ? { aspectRatio: mediaAspectRatio } : {}),
        }"
      >
        <MediaCard
          :media-type="card?.mediaField?.type"
          :image-props="CssImageHelpers.imageProps(card.mediaField?.data)"
          :video-props="CssVideoHelpers.videoProps(card.mediaField?.data)"
          :mask-props="card?.maskField?.data"
          :object-fit="mediaObjectFit"
          :border-radius="card?.mediaField?.data?.borderRadius"
          class="h-full"
        />
      </div>

      <!-- Текстовая секция -->
      <div
        v-if="mainGroup.isShowCardText"
        class="vertical-card-text"
        :style="[
          CssStyleHelpers.margins({ 
            top: cardsGroup.cardMediaTextSpacing ? cardsGroup.cardMediaTextSpacing : 0,
            bottom: 5,
          }),

          CssStyleHelpers.background(
            props.card?.textBackgroundField?.data?.value,
            props.card?.textBackgroundToggleField?.data?.value
          ),
          
          CssStyleHelpers.paddings(cardsGroup.cardTextSpacing),
          CssStyleHelpers.borderRadius(props.card?.textBackgroundRadiusField?.data?.value ?? 0),
          CssStyleHelpers.filterShadow(props.card?.textBackgroundShadowField?.data),
        ]"
      >
        <TextCard :text="card?.textField?.data?.text"  />
      </div>
      
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import MediaCard from "./MediaCard.vue";
import TextCard from "./TextCard.vue";
import {
  CssImageHelpers,
  CssStyleHelpers,
  CssVideoHelpers,
} from "../utils/cssStyleHelpers";

interface VerticalCardProps {
  // Данные карточки
  card: any;
  // Группа настроек карточек
  cardsGroup?: any;
  mainGroup?: any;
  maxImageHeight?: number;
}

const props = defineProps<VerticalCardProps>();

// Вычисляем aspect ratio медиа для правильного отображения
const mediaAspectRatio = computed(() => {
  const mediaData = props.card?.mediaField?.data;
  if (!mediaData) return undefined;

  // Если есть маска, используем aspect ratio маски
  if (props.card?.maskField?.data?.enabled && props.card?.maskField?.data?.maskAspectRatio) {
    return props.card.maskField.data.maskAspectRatio;
  }

  // Иначе используем aspect ratio из медиа
  const width = mediaData.width || mediaData.videoWidth;
  const height = mediaData.height || mediaData.videoHeight;
  
  if (width && height && height !== 0) {
    return width / height;
  }
  
  return undefined;
});

// Определяем, нужно ли растягивать по высоте на основе toggle поля
const shouldStretchHeight = computed(() => {
  // Используем значение из toggle поля
  return props.card?.mediaStretchToggleField?.data?.value === true;
});

// Перевірка наявності реального медіа-контенту
const hasMediaContent = computed(() => {
  const data = props.card?.mediaField?.data;
  return !!(data?.url || data?.previewUrl);
});

// Вирівнювання контенту по вертикалі
const verticalAlignStyle = computed(() => {
  const align = props.card?.textAlignField?.data?.value;
  if (!align || align === 'flex-start') return {};
  return { display: 'flex', alignItems: align, justifyContent: align };
});

const mediaObjectFit = computed<"cover" | "contain">(() => {
  const customFit = props.card?.mediaField?.data?.objectFit;
  if (customFit === "cover" || customFit === "contain") {
    return customFit;
  }
  return shouldStretchHeight.value ? "cover" : "contain";
});
</script>

<style scoped>
@reference "tailwindcss";
.vertical-card {
  @apply w-full h-full overflow-hidden;
}

.vertical-card-content {
  @apply w-full overflow-hidden;
}

.vertical-card-content.h-full {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.vertical-card-content:not(.h-full) {
  height: auto;
  display: block;
}

/* На мобилке отключаем растягивание через CSS медиа-запрос */
@media (max-width: 640px) {
  .vertical-card {
    height: auto !important;
  }
  
  .vertical-card-content {
    @apply h-auto;
    display: block !important;
    height: auto !important;
  }
  
  .vertical-card-content.h-full {
    display: block !important;
    height: auto !important;
  }
}

.vertical-card-media {
  @apply w-full overflow-hidden;
  /* margin-bottom: 1rem; */
}

/* Для горизонтальных изображений (aspect ratio > 1) */
.vertical-card-content.h-full .vertical-card-media {
  flex: 1 1 auto;
  min-height: 0;
}

/* Для квадратных и вертикальных изображений (aspect ratio <= 1) */
.vertical-card-content:not(.h-full) .vertical-card-media {
  flex: none;
}

/* Для десктопа: aspect-ratio устанавливается через inline style */
/* Это позволяет сохранить пропорции каждой картинки */

@media (max-width: 640px) {
  .vertical-card-media {
    flex: none;
    aspect-ratio: unset !important; /* На мобилке убираем aspect-ratio */
    height: auto !important; /* На мобилке высота автоматическая */
  }
  
  .vertical-card-content {
    height: auto !important;
  }
  
  .vertical-card {
    height: auto !important;
  }
}

.vertical-card-text {
  @apply text-base leading-relaxed;
}


/* Адаптивность для мобильных устройств */
@media (max-width: 768px) {
  .vertical-card-text {
    @apply text-sm;
  }

  /* .vertical-card-media {
    height: 45% !important;
    margin-bottom: 0.75rem;
  } */
}
</style>

<style>
@reference "tailwindcss";
/* Добавляем базовые стили card-item для совместимости */
.vertical-card {
  @apply text-base leading-relaxed relative z-10 text-black;
}
</style>
